import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PropTypes } from 'prop-types';

import { MetricsContainer, MetricItem } from '../Styles';

import DateTimeSlider from '../general/DateTimeSlider';
import {
   troubleIntervalSelector,
   troubleModeSelector,
   troubleSelectedPlayoutSelector,
} from '../../../../redux/selectors/selectors';
import {
   setTroubleshootingPlayoutFineTimespan,
   setTroubleshootingSelectedAlert,
   syncTroubleshootingIngest,
} from '../../../../redux/actions/general';
import { E2E } from '../../../../constants/general';
import { URL_PARAMS } from '../../../../urlSync/urlParamNames';
import { getIntervalStart, getIntervalEnd, getStartEnd } from '../general/DataProcessing';
import DefaultPlayoutMetric from './widgets/DefaultPlayoutMetric';
import EventTable from './EventTable';
import { Button } from 'reactstrap';
import { ANNOTATIONS_LIMIT, CHRONOLOGICAL_DATE_TIME_FORMAT, MAX_RAW_MODE_TIME_RANGE_IN_SECS } from '../constants';
import DefaultPlayoutRawMetric from './widgets/DefaultPlayoutRawMetric';
import { bufferingEventTimeChartOptions, rawEventChartOptions } from '../../../../constants/chartjs';
import { CenterContainer, SimpleHorizontalSeparator } from '../../../../styledComponents/styledComponents';
import axios from 'axios';
import { BINTU_API_URL } from '../../../../constants/config';
import { toast } from 'react-toastify';

function PlayoutMetrics({ playoutStart, playoutEnd, streamName, events }) {
   const dispatch = useDispatch();
   const mode = useSelector(troubleModeSelector);
   const { playerId, playoutFineStart, playoutFineEnd } = useSelector(troubleSelectedPlayoutSelector);
   const interval = useSelector(troubleIntervalSelector);
   const { start, end } = getStartEnd(playoutFineStart, playoutFineEnd, interval);
   const [annotationsSize, setAnnotationsSize] = useState(0);
   const setFineInterval = useCallback(
      (fineInterval) => {
         const newPlayoutFineInterval = {
            playoutFineStart: fineInterval.fineStart,
            playoutFineEnd: fineInterval.fineEnd,
         };
         dispatch(
            setTroubleshootingPlayoutFineTimespan(newPlayoutFineInterval, {
               [URL_PARAMS.TROUBLE_PLAYOUT_FINE_START]: fineInterval.fineStart,
               [URL_PARAMS.TROUBLE_PLAYOUT_FINE_END]: fineInterval.fineEnd,
            }),
         );
      },
      [dispatch],
   );

   const urlSyncParams = (streamName) => ({
      payload: {
         streamName,
         publishStart: start.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         publishEnd: end.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         publishFineStart: start.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         publishFineEnd: end.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         troubleMode: E2E,
      },
      pushToUrl: {
         [URL_PARAMS.TROUBLE_INGEST_STREAM]: streamName,
         [URL_PARAMS.TROUBLE_INGEST_PUBLISH_START]: start.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         [URL_PARAMS.TROUBLE_INGEST_PUBLISH_END]: end.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_START]: start.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_END]: end.format(CHRONOLOGICAL_DATE_TIME_FORMAT),
         [URL_PARAMS.TROUBLE_MODE]: E2E,
      },
   });
   const handleAnnotationToggle = (amount) => {
      if (amount > ANNOTATIONS_LIMIT) setAnnotationsSize(amount);
   };

   useEffect(() => {
      if (annotationsSize > ANNOTATIONS_LIMIT)
         toast.info(
            `Found ${annotationsSize} playout events in the selected time range. Disabling event visualization in the charts.`,
         );
   }, [annotationsSize]);

   const isRawModeActive = end.unix() - start.unix() <= MAX_RAW_MODE_TIME_RANGE_IN_SECS;

   return (
      <>
         <SimpleHorizontalSeparator spacePx={20} border={'1px dotted  rgb(180,180,180)'} />
         <DateTimeSlider
            startDateString={getIntervalStart(playoutStart, interval.value)}
            endDateString={getIntervalEnd(playoutEnd, interval.value)}
            fineStartString={playoutFineStart}
            fineEndString={playoutFineEnd}
            setFineInterval={setFineInterval}
            interval={interval}
         />
         <CenterContainer marginTopPx={10}>
            <Button
               className='ml-2'
               color='primary'
               onClick={async () => {
                  getPassthruStream(streamName).then((passthru) => {
                     const { payload, pushToUrl } = urlSyncParams(passthru);
                     dispatch(syncTroubleshootingIngest(payload, pushToUrl));
                  });
                  dispatch(
                     setTroubleshootingSelectedAlert(
                        {
                           alertId: '',
                           alertFineStart: '',
                           alertFineEnd: '',
                        },
                        {
                           [URL_PARAMS.ALERT_ID]: '',
                           [URL_PARAMS.ALERT_FINE_START]: '',
                           [URL_PARAMS.ALERT_FINE_END]: '',
                        },
                     ),
                  );
               }}
            >
               {'Synchronize ingest'}
            </Button>
         </CenterContainer>
         <MetricsContainer>
            <MetricItem isFullScreen={mode !== E2E}>
               {isRawModeActive ? (
                  <DefaultPlayoutRawMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'bitrate'}
                     title={'Bitrate [kBits/s]'}
                     streamName={streamName}
                     events={events}
                     chartOptionFn={rawEventChartOptions}
                     csvFilename={'Playout_Bitrate'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               ) : (
                  <DefaultPlayoutMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'bitrate'}
                     title={'Bitrate [kBits/s]'}
                     streamName={streamName}
                     events={events}
                     csvFilename={'Playout_Bitrate'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               )}
            </MetricItem>
            <MetricItem isFullScreen={mode !== E2E}>
               {isRawModeActive ? (
                  <DefaultPlayoutRawMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'latency'}
                     title={'Latency [s]'}
                     streamName={streamName}
                     events={events}
                     chartOptionFn={rawEventChartOptions}
                     csvFilename={'Playout_Latency'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               ) : (
                  <DefaultPlayoutMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'latency'}
                     title={'Latency [s]'}
                     streamName={streamName}
                     events={events}
                     csvFilename={'Playout_Latency'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               )}
            </MetricItem>
            <MetricItem isFullScreen={mode !== E2E}>
               {isRawModeActive ? (
                  <DefaultPlayoutRawMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'buffering'}
                     title={'Buffering'}
                     streamName={streamName}
                     events={events}
                     chartOptionFn={bufferingEventTimeChartOptions}
                     csvFilename={'Playout_Buffering'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               ) : (
                  <DefaultPlayoutMetric
                     start={start}
                     end={end}
                     playerId={playerId}
                     playoutStart={playoutStart}
                     playoutEnd={playoutEnd}
                     urlName={'buffering'}
                     title={'Buffering Totaltime Ratio [%]'}
                     streamName={streamName}
                     events={events}
                     csvFilename={'Playout_Buffering'}
                     onAnnotationLimit={handleAnnotationToggle}
                  />
               )}
            </MetricItem>
            {events && events.length > 0 && (
               <MetricItem isFullScreen={mode !== E2E}>
                  <EventTable events={events} start={start} end={end} streamName={streamName} />
               </MetricItem>
            )}
         </MetricsContainer>
      </>
   );
}

async function getPassthruStream(vtransStream) {
   try {
      const { data } = await axios
         .create({ baseURL: BINTU_API_URL, withCredentials: false })
         .get(`/stream/${vtransStream}/group?profile=light`);
      return data.playout.rtmp[0].streamname;
   } catch (error) {
      return vtransStream;
   }
}

PlayoutMetrics.propTypes = {
   playoutStart: PropTypes.string,
   playoutEnd: PropTypes.string,
   streamName: PropTypes.string,
   events: PropTypes.array,
};

export default PlayoutMetrics;
